import { useRef, useLayoutEffect, useMemo } from "react";
import { Box, SystemStyleObject } from "@chakra-ui/react";
import parse, { attributesToProps, Element } from "html-react-parser";
import { ParsedHtmlProps } from "./ParsedHtml.props";

/**
 * Base styles for rendering HTML content.
 * Uses nested selectors with "& selector" pattern for the css prop.
 */
const baseHtmlStyles: SystemStyleObject = {
  fontFamily: "var(--chakra-fonts-body)",
  width: "100%",
  "& a": {
    cursor: "pointer",
    color: "secondary.600",
    textDecoration: "underline",
  },
  "& img": {
    maxWidth: "100%",
    height: "auto",
    display: "inline-block",
  },
  "& iframe": {
    width: "100%",
  },
  "& strong": {
    fontWeight: 600,
  },
  "& em": {
    fontStyle: "italic",
  },
  "& ol, & ul": {
    marginLeft: "30px",
  },
  "& ul": {
    listStyle: "disc",
  },
  "& ol": {
    listStyle: "decimal",
  },
  "& h1, & h2, & h3, & h4, & h5, & h6": {
    marginBottom: "15px",
    marginTop: "0px !important",
    fontWeight: 700,
  },
  "& p, & ol, & ul": {
    marginBottom: "15px",
    marginTop: "0px !important",
  },
  "& h1": {
    fontSize: "32px",
    lineHeight: "32px",
  },
  "& h2": {
    fontSize: "28px",
    lineHeight: "28px",
  },
  "& h3": {
    fontSize: "24px",
    lineHeight: "24px",
  },
  "& h4": {
    fontSize: "20px",
    lineHeight: "20px",
  },
  "& h5": {
    fontSize: "18px",
    lineHeight: "18px",
  },
  "& h6": {
    fontSize: "16px",
    lineHeight: "16px",
  },
  "& p, & li, & blockquote p": {
    fontSize: "16px",
  },
  "& code": {
    fontFamily: '"Courier New"',
    color: "fg.muted",
    fontWeight: "normal",
  },
};

/**
 * Converts a CSS string (e.g., "margin-bottom: 8px; color: red;") to a style object.
 * Handles kebab-case to camelCase conversion for property names.
 */
function parseCssString(cssString: string): Record<string, string> {
  const result: Record<string, string> = {};
  const declarations = cssString.split(";").filter((s) => s.trim());

  // eslint-disable-next-line no-restricted-syntax -- for loops are faster than built-ins
  for (const declaration of declarations) {
    const colonIndex = declaration.indexOf(":");
    // eslint-disable-next-line no-continue
    if (colonIndex === -1) continue;

    const property = declaration.slice(0, colonIndex).trim();
    const value = declaration.slice(colonIndex + 1).trim();

    // Convert kebab-case to camelCase (e.g., "margin-bottom" -> "marginBottom")
    const camelCaseProperty = property.replace(/-([a-z])/g, (_, letter) =>
      letter.toUpperCase()
    );

    result[camelCaseProperty] = value;
  }

  return result;
}

/**
 * Converts elementStyles (Record<selector, cssString>) to a SystemStyleObject.
 * Prepends "& " to selectors for proper nesting in the css prop.
 */
function convertElementStylesToObject(
  elementStyles: Record<string, string>
): Record<string, Record<string, string>> {
  const result: Record<string, Record<string, string>> = {};

  // eslint-disable-next-line no-restricted-syntax -- for loops are faster than built-ins
  for (const [selector, cssString] of Object.entries(elementStyles)) {
    const nestedSelector = `& ${selector}`;
    result[nestedSelector] = parseCssString(cssString);
  }

  return result;
}

/**
 * This component is used to parse html snippets stored as strings (ex: "<p>this was written in a text editor</p>")
 * It includes styling for bullet points and links
 */
const ParsedHtml = ({
  htmlString,
  truncateTextLines = undefined,
  onTruncateChange,
  elementStyles,
}: ParsedHtmlProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const lastIsTruncatedRef = useRef<boolean>();

  // Memoize the combined styles to prevent recalculation on every render
  const combinedStyles = useMemo(() => {
    if (!elementStyles) {
      return baseHtmlStyles;
    }
    return {
      ...baseHtmlStyles,
      ...convertElementStylesToObject(elementStyles),
    };
  }, [elementStyles]);

  useLayoutEffect(() => {
    if (!containerRef.current || !onTruncateChange) return undefined;

    // function to check and notify if truncated
    const checkTruncate = () => {
      const el = containerRef.current;
      const isTruncated = !!el && el.scrollHeight > el.clientHeight;

      if (isTruncated !== lastIsTruncatedRef.current) {
        lastIsTruncatedRef.current = isTruncated;
        onTruncateChange(isTruncated);
      }
    };

    // initial check
    checkTruncate();

    // observe for any size changes
    let ro: ResizeObserver | undefined;
    if (window.ResizeObserver) {
      ro = new ResizeObserver(checkTruncate);
      ro.observe(containerRef.current);
    }

    return () => ro?.disconnect();
  }, [onTruncateChange, htmlString]);

  return (
    <Box ref={containerRef} lineClamp={truncateTextLines} css={combinedStyles}>
      {parse(htmlString, {
        // Custom DOM element replace function
        // This is to inject a containing element around iframes and to inject styling into the iframe tags being parsed.
        // Since we allow a limited amount of sites using our CSP, it is assumed that the iframes will be containing a video,
        // therefore we set a width and height that maintains a 16:9 aspect ratio.
        replace(domNode) {
          if (domNode.type === "tag") {
            const domElement = domNode as Element;
            if (domElement.name === "iframe") {
              return (
                <div
                  style={{
                    position: "relative",
                    width: "100%",
                    paddingTop: "56.25%", // Maintain a 16:9 aspect ratio (9 / 16)
                  }}
                >
                  <iframe
                    title={domElement.attribs.title}
                    {...attributesToProps(domElement.attribs)}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                    }}
                  />
                </div>
              );
            }
          }
          // We don't need a consistent return here as returning nothing means nothing is replaced, and we only want to replace iframes to inject styling.
          // eslint-disable-next-line consistent-return, no-useless-return
          return;
        },
      })}
    </Box>
  );
};

export default ParsedHtml;
